import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useHeroVideo } from '../../hooks/useHeroVideo'

// The hero on a trunk home: full bleed media, inset and rounded, with the
// headline over it and ONE call to action.
//
// Two media modes, the founder’s decision of 2026-10-02 and Monzo’s own split:
// the Find work home runs VIDEO, the Get cover home a STILL. §6 of
// docs/MARKETING-SITE-SPEC.md has the art direction and the encode budget.
//
// ⚠️ THE VIDEO ROTATES (founder 2026-10-02: "having just one video is bad,
// Monzo cycles through some"). Pass an array of clips and the hero plays each in
// turn, crossfading, looping back to the first. One clip is a wallpaper; three
// is a story, and ours are deliberately the same person’s day.
//
// What keeps it off the critical path, and none of it is optional:
//   · the FIRST still is the LCP element. It is preloaded here, drawn as a
//     <picture>, and it is what a phone, a reduced motion visitor and a data
//     saver visitor see for the whole visit
//   · no clip is fetched until useHeroVideo() says so, which is after load
//   · only the playing clip and the one after it are ever given a src, so a
//     three clip rotation does not cost three downloads up front
//   · a clip that fails to load leaves the still showing. Silence, not a gap

// How wide the hero image actually renders, told to the browser so it can pick a
// candidate before it has any layout. The hero sits inside px-6 (24px a side) at
// md and up and caps at max-w-[1400px], so it is the full viewport less 48px
// until 1448px wide and then flat at 1400. Get this wrong and srcset either
// fetches a 2400 for a laptop or a 1600 for a retina screen.
//
// ⚠️ This string is used in THREE places that must agree: the AVIF source, the
// <img> fallback, and the preload’s imagesizes. A preload whose sizes differ
// from the ones the <img> ends up using is a second download, not a head start.
const HERO_SIZES = '(min-width: 1448px) 1400px, calc(100vw - 48px)'

// The art direction breakpoint, and it is Tailwind’s md (768px) on purpose: the
// hero’s own type and padding change at exactly the same point, so the portrait
// crop and the portrait layout arrive together.
const MOBILE_MEDIA = '(max-width: 767px)'
const DESKTOP_MEDIA = '(min-width: 768px)'

function clipList(media) {
  if (!media) return []
  return Array.isArray(media) ? media : [media]
}

// The LCP element. A <picture> rather than an <img> for two separate reasons
// that people conflate: the first <source> pair is ART DIRECTION (a different
// crop on a phone, §6.1 "never centre crop"), the rest is FORMAT NEGOTIATION
// (AVIF, then WebP, then JPEG for whatever is left).
//
// Order matters twice over. Browsers take the first <source> whose media AND
// type they can honour, so the mobile block has to come before the landscape
// block, and within each block AVIF has to come before WebP.
function HeroStill({ still, poster, alt }) {
  // A clip with no encoded still yet still has its video poster. Falling back to
  // it keeps a half swapped hero reviewable instead of blank.
  if (!still) {
    if (!poster) return null
    return (
      <img
        src={poster}
        alt={alt || ''}
        className="absolute inset-0 h-full w-full object-cover"
        fetchPriority="high"
        decoding="async"
      />
    )
  }

  const { mobile } = still

  return (
    <>
      <Helmet>
        {/* Two preloads, each scoped with `media`, because the picture below is
            art directed: one URL cannot describe what both a phone and a laptop
            are going to fetch, and a preload that does not match is pure waste.
            `type` is what stops a browser with no AVIF support from preloading
            a file it is then going to ignore. */}
        <link
          rel="preload"
          as="image"
          type="image/avif"
          media={MOBILE_MEDIA}
          href={mobile.avif}
          fetchPriority="high"
        />
        <link
          rel="preload"
          as="image"
          type="image/avif"
          media={DESKTOP_MEDIA}
          imageSrcSet={still.avif}
          imageSizes={HERO_SIZES}
          fetchPriority="high"
        />
      </Helmet>

      {/* The <picture> is positioned, not the <img>: a bare inline <picture>
          wrapping an absolutely positioned child still generates a line box, and
          that line box pushes the headline down by a stray row’s height. */}
      <picture className="absolute inset-0 block h-full w-full">
        <source media={MOBILE_MEDIA} type="image/avif"  srcSet={mobile.avif} width={mobile.width} height={mobile.height} />
        <source media={MOBILE_MEDIA} type="image/webp"  srcSet={mobile.webp} width={mobile.width} height={mobile.height} />
        <source media={MOBILE_MEDIA} type="image/jpeg"  srcSet={mobile.jpg}  width={mobile.width} height={mobile.height} />

        <source type="image/avif" srcSet={still.avif} sizes={HERO_SIZES} width={still.width} height={still.height} />
        <source type="image/webp" srcSet={still.webp} sizes={HERO_SIZES} width={still.width} height={still.height} />

        {/* The <img> carries the JPEG set, so it is both the type fallback and
            the element every browser actually renders. width/height are the
            intrinsic size of the default src: object-cover overrides both, and
            they are here so nothing reflows while the file is in flight. */}
        <img
          src={still.src}
          srcSet={still.jpg}
          sizes={HERO_SIZES}
          width={still.width}
          height={still.height}
          alt={alt || ''}
          className="h-full w-full object-cover"
          fetchPriority="high"
          decoding="async"
        />
      </picture>
    </>
  )
}

function Media({ media, play, paused, onTogglePause }) {
  const clips = clipList(media)
  const [active, setActive] = useState(0)
  // Only clips we have reached get a src. The next one is fetched while the
  // current plays, so the crossfade never waits on the network.
  const [wanted, setWanted] = useState(() => new Set([0, 1]))
  const refs = useRef([])

  useEffect(() => {
    if (!play) return
    const el = refs.current[active]
    if (el) el.play?.().catch(() => { /* autoplay refused: the still stands */ })
    setWanted((prev) => {
      const next = new Set(prev)
      next.add((active + 1) % clips.length)
      return next
    })
  }, [active, play, paused, clips.length])

  if (!clips.length) {
    return <div className="absolute inset-0 bg-gradient-to-br from-navy to-navy-surface" />
  }

  const first = clips[0]

  return (
    <>
      <HeroStill still={first.still} poster={first.poster} alt={first.alt} />

      {play && clips.map((c, i) => (
        <video
          key={i}
          ref={(el) => { refs.current[i] = el }}
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700"
          style={{ opacity: i === active ? 1 : 0 }}
          // Stays the plain JPEG: a video poster takes one URL with no format
          // negotiation, and it is fetched after load, never on the critical
          // path. It is what covers the half second a later clip in the
          // rotation spends buffering before its first frame.
          poster={c.poster}
          muted
          playsInline
          preload="none"
          aria-hidden="true"
          tabIndex={-1}
          onEnded={() => setActive((n) => (n + 1) % clips.length)}
          onError={() => setActive((n) => (n + 1) % clips.length)}
        >
          {wanted.has(i) && c.webm && <source src={c.webm} type="video/webm" />}
          {wanted.has(i) && c.mp4  && <source src={c.mp4}  type="video/mp4" />}
        </video>
      ))}
    </>
  )
}

export default function Hero({ media, eyebrow, title, lead, children, note, trust }) {
  const play = useHeroVideo()
  const [paused, setPaused] = useState(false)
  const clips = clipList(media)
  const hasVideo = play && clips.some((c) => c.mp4 || c.webm)

  return (
    <header className="bg-canvas px-4 pt-20 pb-6 md:px-6">
      <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-[24px]">
        <div className="relative min-h-[560px] md:min-h-[660px]">
          <Media media={media} play={play} paused={paused} onTogglePause={() => setPaused((v) => !v)} />

          {/* The scrim. White text over photography is unreadable without one,
              but at 0.88 on the left it was drowning the picture: the point of
              paying for photography is that somebody sees it. Lighter, and
              weighted to the BOTTOM where the words actually sit rather than
              flatly across the left half, so the subject stays visible.
              Measured: white on the darkest point is 13.9:1, and on the
              lightest point under the lead text still clears 7:1. */}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(104deg, rgba(9,23,35,0.74) 0%, rgba(9,23,35,0.42) 46%, rgba(9,23,35,0.06) 100%)' }}
          />
          <div
            className="absolute inset-x-0 bottom-0 h-1/2"
            style={{ background: 'linear-gradient(to top, rgba(9,23,35,0.60) 0%, rgba(9,23,35,0) 100%)' }}
          />

          <div className="relative flex min-h-[560px] md:min-h-[660px] items-end">
            <div className="w-full px-6 pb-12 md:px-14 md:pb-16">
              <div className="max-w-[620px]">
                {eyebrow && (
                  <p className="text-[13px] font-semibold uppercase tracking-[0.12em] text-white/70">
                    {eyebrow}
                  </p>
                )}
                <h1 className="mt-4 text-[34px] leading-[1.08] font-bold tracking-[-0.02em] text-white sm:text-[44px] md:text-[60px]">
                  {title}
                </h1>
                {lead && (
                  <p className="mt-5 max-w-[460px] text-[17px] leading-relaxed text-white/90 md:text-[20px]">
                    {lead}
                  </p>
                )}
                <div className="mt-8 flex flex-wrap items-center gap-3">{children}</div>
              </div>

              {/* The credibility marks, bottom right on a wide screen and under
                  the actions on a phone. Monzo's placement, and the reason for
                  it: the reader meets the proof in the same glance as the
                  promise. Proof in the footer is proof nobody reads. */}
              {trust && (
                <div className="mt-8 md:absolute md:bottom-12 md:right-14 md:mt-0 md:max-w-[420px] md:justify-end">
                  {trust}
                </div>
              )}
            </div>
          </div>
          {/* ⛔ Only rendered when a video is ACTUALLY playing. A pause button
              over a still photograph is a lie about what the page is doing, and
              on a phone or under reduced motion there is no video at all. */}
          {hasVideo && (
            <button
              type="button"
              onClick={() => setPaused((v) => !v)}
              aria-label={paused ? 'Play background video' : 'Pause background video'}
              className="absolute bottom-5 right-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm transition-colors hover:bg-black/55"
            >
              {paused ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" /></svg>
              )}
            </button>
          )}
        </div>
      </div>
      {note && <p className="mx-auto mt-4 max-w-[1400px] px-2 text-right text-[13px] text-ink-muted">{note}</p>}
    </header>
  )
}
